import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { TableModule } from '@lifeomic/chroma-react/components/TableModule';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Box } from '@lifeomic/chroma-react/components/Box';
import FhirService, {
  type Resource,
} from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';
import { useActiveAccountId } from '@lifeomic/phc-web-toolkit/src/hooks';
import { pwtSharedMessages } from '@lifeomic/phc-web-toolkit/src/common/intlLabels';
import FhirDateTime from '@lifeomic/phc-web-toolkit/src/util/fhirResourceParsers/FhirDateTime';
import { memberDetailMessages } from './messages';
import { useDataset } from '../../../../util/api';
import { ExtractedResourceProvenance } from './ExtractedResourceProvenance';

const immunizationFhirService = new FhirService('Immunization' as Resource);
const dateTimeParser = new FhirDateTime();

type CodeableConcept = {
  coding?: Array<{ display?: string; code?: string }>;
  text?: string;
};

export type ImmunizationResource = fhir.Resource & {
  id?: string;
  identifier?: Array<{ system?: string; value?: string }>;
  vaccineCode?: CodeableConcept;
  status?: string;
  occurrenceDateTime?: string;
  occurrenceString?: string;
  primarySource?: boolean;
  lotNumber?: string;
  expirationDate?: string;
  site?: CodeableConcept;
  route?: CodeableConcept;
  doseQuantity?: {
    value?: number;
    unit?: string;
    code?: string;
  };
  performer?: Array<{
    actor?: {
      display?: string;
      reference?: string;
    };
  }>;
  note?: Array<{ text?: string }>;
};

const useStyles = makeStyles(theme => ({
  tabPanel: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    backgroundColor: theme.palette.background.paper,
    position: 'relative',
  },
  vaccineCell: {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  },
  modalSection: {
    marginBottom: 24,
  },
  modalField: {
    marginBottom: 12,
  },
  modalLabel: {
    display: 'block',
    marginBottom: 4,
    fontWeight: 600,
  },
  modalValue: {
    display: 'block',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    lineHeight: 1.5,
  },
}));

export const getCodeableConceptDisplay = (
  concept: CodeableConcept | undefined,
): string =>
  concept?.coding?.find(coding => coding.display)?.display ||
  concept?.coding?.[0]?.code ||
  concept?.text ||
  '—';

export const getVaccineDisplay = (resource: ImmunizationResource): string =>
  getCodeableConceptDisplay(resource.vaccineCode);

export const getOccurrenceDisplay = (
  resource: ImmunizationResource,
): string => {
  if (resource.occurrenceDateTime) {
    return dateTimeParser.getDisplayValue(resource.occurrenceDateTime) || '—';
  }
  return resource.occurrenceString || '—';
};

export const getImmunizationSortTime = (
  resource: ImmunizationResource,
): number =>
  resource.occurrenceDateTime
    ? new Date(resource.occurrenceDateTime).getTime()
    : 0;

export const getPerformerDisplay = (resource: ImmunizationResource): string =>
  resource.performer?.[0]?.actor?.display ||
  resource.performer?.[0]?.actor?.reference ||
  '—';

const getDoseDisplay = (resource: ImmunizationResource): string => {
  if (!resource.doseQuantity) {
    return '—';
  }
  const { value, unit, code } = resource.doseQuantity;
  return [value, unit || code].filter(valuePart => valuePart != null).join(' ');
};

type ImmunizationsRow = {
  id: string;
  resource: ImmunizationResource;
  vaccine: string;
  status: string;
  occurrence: string;
  performer: string;
  lotNumber: string;
};

type ImmunizationViewModalProps = {
  resource: ImmunizationResource;
  subjectId: string;
  onDismiss: () => void;
  onRejected: (resourceId: string) => void;
};

export const ImmunizationViewModal: React.FC<ImmunizationViewModalProps> = ({
  resource,
  subjectId,
  onDismiss,
  onRejected,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const vaccine = getVaccineDisplay(resource);
  const occurrence = getOccurrenceDisplay(resource);
  const performer = getPerformerDisplay(resource);
  const site = getCodeableConceptDisplay(resource.site);
  const route = getCodeableConceptDisplay(resource.route);
  const dose = getDoseDisplay(resource);
  const primarySource =
    resource.primarySource === undefined
      ? '—'
      : resource.primarySource
      ? 'Yes'
      : 'No';
  const note = resource.note?.[0]?.text;

  return (
    <Modal
      isOpen={true}
      size={1}
      fullWidth
      onDismiss={onDismiss}
      title={vaccine}
      data-testid="immunization-view-modal"
    >
      <Box padding={2} direction="column">
        <div className={classes.modalSection}>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.immunizationsVaccine)}
            </span>
            <span className={classes.modalValue}>{vaccine}</span>
          </div>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.immunizationsStatus)}
            </span>
            <span className={classes.modalValue}>{resource.status || '—'}</span>
          </div>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.immunizationsOccurrence)}
            </span>
            <span className={classes.modalValue}>{occurrence}</span>
          </div>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.immunizationsPerformer)}
            </span>
            <span className={classes.modalValue}>{performer}</span>
          </div>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.immunizationsLotNumber)}
            </span>
            <span className={classes.modalValue}>
              {resource.lotNumber || '—'}
            </span>
          </div>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.immunizationsExpirationDate)}
            </span>
            <span className={classes.modalValue}>
              {resource.expirationDate
                ? dateTimeParser.getDisplayValue(resource.expirationDate) || '—'
                : '—'}
            </span>
          </div>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.immunizationsPrimarySource)}
            </span>
            <span className={classes.modalValue}>{primarySource}</span>
          </div>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.immunizationsSite)}
            </span>
            <span className={classes.modalValue}>{site}</span>
          </div>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.immunizationsRoute)}
            </span>
            <span className={classes.modalValue}>{route}</span>
          </div>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.immunizationsDose)}
            </span>
            <span className={classes.modalValue}>{dose || '—'}</span>
          </div>
          {note && (
            <div className={classes.modalField}>
              <span className={classes.modalLabel}>
                {formatMessage(memberDetailMessages.immunizationsNote)}
              </span>
              <span className={classes.modalValue}>{note}</span>
            </div>
          )}
        </div>
        <ExtractedResourceProvenance
          resource={resource}
          resourceType="Immunization"
          subjectId={subjectId}
          onRejected={onRejected}
        />
      </Box>
    </Modal>
  );
};

export type ImmunizationsContentProps = {
  memberId: string;
  fallback: React.ReactNode;
};

export const ImmunizationsContent: React.FC<ImmunizationsContentProps> = ({
  memberId,
  fallback,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const accountId = useActiveAccountId();
  const auth = useServiceAuth();
  const dataset = useDataset();
  const [fetchImmunizations, fetchState] = useService(
    immunizationFhirService.get,
  );
  const [immunizations, setImmunizations] = useState<ImmunizationResource[]>(
    [],
  );
  const [selectedImmunization, setSelectedImmunization] =
    useState<ImmunizationResource | null>(null);

  const loadImmunizations = useCallback(async () => {
    if (!accountId || !memberId || !auth) return;
    try {
      const response = await fetchImmunizations(accountId, {
        patient: memberId,
        _tag: [dataset.codingToken],
        _count: 200,
      });
      const entry = response?.data?.entry ?? [];
      const resources = entry
        .map(e => e.resource as ImmunizationResource | undefined)
        .filter(Boolean) as ImmunizationResource[];
      const sortedResources = resources.sort((a, b) => {
        const dateA = getImmunizationSortTime(a);
        const dateB = getImmunizationSortTime(b);
        return dateB - dateA;
      });
      setImmunizations(sortedResources);
    } catch {
      setImmunizations([]);
    }
  }, [accountId, memberId, auth, fetchImmunizations, dataset.codingToken]);

  useEffect(() => {
    void loadImmunizations();
  }, [loadImmunizations]);

  const rows: ImmunizationsRow[] = immunizations.map(resource => ({
    id: resource.id ?? '',
    resource,
    vaccine: getVaccineDisplay(resource),
    status: resource.status || '—',
    occurrence: getOccurrenceDisplay(resource),
    performer: getPerformerDisplay(resource),
    lotNumber: resource.lotNumber || '—',
  }));

  const columns = [
    {
      header: {
        label: formatMessage(memberDetailMessages.immunizationsVaccine),
      },
      cell: {
        content: (row: ImmunizationsRow) => row.vaccine,
        className: classes.vaccineCell,
      },
    },
    {
      header: {
        label: formatMessage(memberDetailMessages.immunizationsStatus),
      },
      cell: { content: (row: ImmunizationsRow) => row.status },
    },
    {
      header: {
        label: formatMessage(memberDetailMessages.immunizationsOccurrence),
      },
      cell: { content: (row: ImmunizationsRow) => row.occurrence },
    },
    {
      header: {
        label: formatMessage(memberDetailMessages.immunizationsPerformer),
      },
      cell: { content: (row: ImmunizationsRow) => row.performer },
    },
    {
      header: {
        label: formatMessage(memberDetailMessages.immunizationsLotNumber),
      },
      cell: { content: (row: ImmunizationsRow) => row.lotNumber },
    },
  ];

  const handleRowClick = (row: ImmunizationsRow) => {
    setSelectedImmunization(row.resource);
  };

  if (fetchState.isFetching && immunizations.length === 0) {
    return <>{fallback}</>;
  }

  return (
    <div className={classes.tabPanel}>
      <TableModule
        isLoading={fetchState.isFetching}
        config={columns}
        data={rows}
        noResultsMessage={formatMessage(
          memberDetailMessages.immunizationsEmpty,
        )}
        onRowClick={handleRowClick}
        rowClickLabel={formatMessage(pwtSharedMessages.viewDetails)}
        rowRole="link"
      />
      {selectedImmunization && (
        <ImmunizationViewModal
          resource={selectedImmunization}
          subjectId={memberId}
          onDismiss={() => setSelectedImmunization(null)}
          onRejected={resourceId => {
            setImmunizations(current =>
              current.filter(resource => resource.id !== resourceId),
            );
            setSelectedImmunization(null);
          }}
        />
      )}
    </div>
  );
};
